/**
 * Monthly Insurance Invoice Service
 * Handles API calls for monthly insurance invoice workflow
 */

import api from './api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export interface MonthlyInvoice {
  id: number;
  numero: string;
  assureur: number;
  assureur_name: string;
  month: number;
  year: number;
  period_start: string;
  period_end: string;
  total_amount: string;
  remise_percentage: string;
  remise_amount: string;
  net_to_pay: string;
  number_of_patients: number;
  number_of_invoices: number;
  status: 'draft' | 'generated' | 'submitted' | 'partially_paid' | 'paid' | 'rejected' | 'cancelled';
  status_display: string;
  generated_at?: string;
  submitted_at?: string;
  expected_payment_date?: string;
  actual_payment_date?: string;
  amount_paid: string;
  is_overdue: boolean;
  days_overdue: number;
  payment_percentage: number | string; // Can be number or string from DecimalField
  pdf_file?: string;
  created_at: string;
  updated_at: string;
}

export interface MonthlyInvoiceDetail extends Omit<MonthlyInvoice, 'assureur'> {
  assureur: {
    id: number;
    nom: string;
    code: string;
    email?: string;
    telephone?: string;
  };
  submission_method?: string;
  submission_reference?: string;
  notes?: string;
  rejection_reason?: string;
  created_by?: number;
  created_by_name?: string;
  payments: Payment[];
}

export interface Payment {
  id: number;
  amount: string;
  payment_date: string;
  payment_method: string;
  reference: string;
  notes: string;
  proof_document?: string;
  recorded_by?: number;
  recorded_by_name?: string;
  created_at: string;
}

export interface GenerateInvoiceRequest {
  assureur_id: number;
  month: number;
  year: number;
  remise_percentage?: string;
}

export interface SubmitInvoiceRequest {
  submission_method: string;
  submission_reference?: string;
  expected_payment_date?: string;
  notes?: string;
}

export interface RecordPaymentRequest {
  amount: string;
  payment_date: string;
  payment_method: string;
  reference?: string;
  notes?: string;
  proof_document?: File;
}

export interface PreviewInvoiceData {
  assureur: string;
  month: number;
  year: number;
  invoice_count: number;
  patient_count: number;
  total_amount: string;
  remise_amount: string;
  net_to_pay: string;
  message?: string;
  invoices?: Array<{
    numero: string;
    patient: string;
    date_emission: string;
    montant_assurance: string;
  }>;
}

class MonthlyInvoiceService {
  private baseURL = `${API_URL}/facturation`;

  /**
   * List all monthly invoices with optional filters
   */
  async listInvoices(params?: {
    assureur_id?: number;
    status?: string;
    year?: number;
    month?: number;
    overdue?: boolean;
  }): Promise<MonthlyInvoice[]> {
    // Add cache-busting timestamp to force fresh data
    const requestParams = {
      ...params,
      _t: Date.now()
    };
    const response = await api.get(`${this.baseURL}/monthly-invoices/`, {
      params: requestParams,
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    });
    // Handle both paginated and direct array responses
    return response.data.results || response.data || [];
  }

  /**
   * Get invoice details by ID
   */
  async getInvoice(id: number): Promise<MonthlyInvoiceDetail> {
    const response = await api.get(`${this.baseURL}/monthly-invoices/${id}/`, {
      params: { _t: Date.now() },
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    });
    return response.data;
  }

  /**
   * Preview invoice data before generation
   */
  async previewInvoice(
    assureurId: number,
    month: number,
    year: number
  ): Promise<PreviewInvoiceData> {
    const response = await api.get(`${this.baseURL}/monthly-invoices/preview/`, {
      params: {
        assureur_id: assureurId,
        month,
        year,
      },
    });
    return response.data;
  }

  /**
   * Generate a new monthly invoice
   */
  async generateInvoice(data: GenerateInvoiceRequest): Promise<MonthlyInvoiceDetail> {
    const response = await api.post(`${this.baseURL}/monthly-invoices/generate/`, data);
    return response.data;
  }

  /**
   * Submit an invoice to insurance company
   */
  async submitInvoice(id: number, data: SubmitInvoiceRequest): Promise<MonthlyInvoiceDetail> {
    const response = await api.post(`${this.baseURL}/monthly-invoices/${id}/submit/`, data);
    return response.data;
  }

  /**
   * Record a payment for an invoice
   */
  async recordPayment(id: number, data: RecordPaymentRequest): Promise<Payment> {
    const formData = new FormData();
    formData.append('amount', data.amount);
    formData.append('payment_date', data.payment_date);
    formData.append('payment_method', data.payment_method);
    if (data.reference) formData.append('reference', data.reference);
    if (data.notes) formData.append('notes', data.notes);
    if (data.proof_document) formData.append('proof_document', data.proof_document);

    const response = await api.post(
      `${this.baseURL}/monthly-invoices/${id}/payments/`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  /**
   * View invoice PDF in browser (opens in new tab)
   */
  async viewPDF(id: number): Promise<void> {
    const url = `${this.baseURL}/monthly-invoices/${id}/view/`;
    window.open(url, '_blank');
  }

  /**
   * Download invoice PDF
   */
  async downloadPDF(id: number, numero: string): Promise<void> {
    const response = await api.get(`${this.baseURL}/monthly-invoices/${id}/download/`, {
      responseType: 'blob',
    });

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${numero}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  /**
   * Delete an invoice completely from database
   */
  async cancelInvoice(id: number): Promise<{ message: string }> {
    const response = await api.delete(`${this.baseURL}/monthly-invoices/${id}/cancel/`);
    return response.data;
  }

  /**
   * Update invoice details
   */
  async updateInvoice(
    id: number,
    data: Partial<MonthlyInvoiceDetail>
  ): Promise<MonthlyInvoiceDetail> {
    const response = await api.patch(`${this.baseURL}/monthly-invoices/${id}/`, data);
    return response.data;
  }

  /**
   * Get month name in French
   */
  getMonthName(month: number): string {
    const months = [
      'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
    ];
    return months[month - 1] || '';
  }

  /**
   * Format currency
   */
  formatCurrency(amount: string | number): string {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('fr-GN', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num) + ' GNF';
  }

  /**
   * Get status color
   */
  getStatusColor(status: string): string {
    const colors = {
      draft: 'gray',
      generated: 'blue',
      submitted: 'orange',
      partially_paid: 'yellow',
      paid: 'green',
      rejected: 'red',
      cancelled: 'gray',
    };
    return colors[status as keyof typeof colors] || 'gray';
  }
}

export default new MonthlyInvoiceService();
